import { useMutation, useQuery } from '@tanstack/react-query'
import { Loader2, RotateCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { friendlyMessage } from '@/api/http'
import { multimodalApi } from '@/api/multimodalApi'
import { chapterApi } from '@/api/chapterApi'
import { AssetProvenanceNotice } from '@/components/multimodal/AssetProvenanceNotice'
import { Button } from '@/components/ui/button'
import { EmptyMultimodalState } from './ReaderMultimodalTabs'

export function ReaderVideoView({
  chapterId,
  posterUrl,
  hasVideo,
}: {
  chapterId: string | null | undefined
  posterUrl?: string | null
  hasVideo: boolean
}) {
  const { t } = useTranslation()
  const assetsQuery = useQuery({ queryKey: ['chapter-assets', chapterId], queryFn: () => chapterApi.assets(chapterId!), enabled: Boolean(chapterId && hasVideo) })
  const videoAsset = [...(assetsQuery.data ?? [])].filter((asset) => asset.asset_type === 'video').sort((a, b) => b.version - a.version)[0]
  const stationStatusQuery = useQuery({
    queryKey: ['video-station-status', chapterId],
    queryFn: () => multimodalApi.getVideoStationStatus(chapterId!),
    enabled: Boolean(chapterId),
    retry: false,
  })
  const regenerateMutation = useMutation({
    mutationFn: () => multimodalApi.regenerateVideo(chapterId!),
    onSuccess: () => toast.success(t('detail.pipelineViewer.queued')),
    onError: (error) => toast.error(friendlyMessage(error, t('detail.pipelineViewer.regenerateFailed'))),
  })
  const stationDisabled = stationStatusQuery.data?.station_disabled !== false
  const actionTitle = stationDisabled
    ? t('errors.codes.video_station_disabled')
    : t('reader.consume.videoGenerate')

  if (!chapterId) {
    return <EmptyMultimodalState>{t('reader.consume.videoEmpty')}</EmptyMultimodalState>
  }

  const regenerateButton = (
    <Button
      type="button"
      variant="outline"
      title={actionTitle}
      aria-label={actionTitle}
      disabled={stationDisabled || regenerateMutation.isPending}
      onClick={() => regenerateMutation.mutate()}
    >
      {regenerateMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <RotateCw className="size-4" />}
      {t('reader.consume.videoGenerate')}
    </Button>
  )

  if (!hasVideo) {
    return (
      <div className="mx-auto flex min-h-[calc(100vh-11rem)] w-full max-w-[760px] flex-col items-center justify-center gap-4 px-4 py-16">
        <div className="rounded-md border border-dashed bg-muted/20 px-6 py-8 text-center text-sm text-muted-foreground">
          {t('reader.consume.videoEmpty')}
        </div>
        {regenerateButton}
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-[920px] px-4 py-8 sm:py-10">
      <div className="mb-3 flex justify-end">{regenerateButton}</div>
      <AssetProvenanceNotice payload={videoAsset?.payload} />
      <video
        src={multimodalApi.videoRawUrl(chapterId)}
        poster={posterUrl || undefined}
        controls
        preload="metadata"
        className="max-h-[calc(100vh-12rem)] w-full rounded-md border bg-muted"
      >
        {t('reader.consume.videoUnsupported')}
      </video>
    </div>
  )
}
